import React, { useContext, createContext } from 'react';
import PropTypes from 'prop-types';
import { UserManager, WebStorageStateStore } from 'oidc-client';

import { CookieStorage } from 'cookie-storage';
import { errorHandler as clientErrorHandler } from '../../helpers/errorHandler';

/**
 * Set expiry on the cookies
 * @NOTE: This doesn't affect the server-side expiry of the session
 */
const getCookieExpiry = () => {
  const now = new Date();
  const later = new Date();
  later.setHours(now.getHours() + 2);

  return later;
};

const COOKIE_DEFAULTS = {
  path: '/',
  secure: true,
  sameSite: 'Strict'
};

const COOKIE_PREFIX = 'sm.oidc.';

/**
 * Creates the state to be shared with the callback URL
 */
function generateState() {
  return {
    hostname: window.location.hostname
  };
}

/**
 * Check if there is an active session and if not create one
 *
 * @NOTE: This is currently non-blocking on purpose
 * to not affect normal execution of the app
 */
function handleSignIn(manager) {
  manager.getUser().then(user => {
    if (!user || !user.access_token) {
      // Check if there is already an active authentication happening
      manager.settings.stateStore.getAllKeys().then(keys => {
        if (!keys.length) {
          // @TODO: Switch this to signinRedirect when we
          // stop only silent authentication
          manager
            .signinSilent({
              // @TODO: Remove this when we stop always logging in silently
              // @NOTE: Need this otherwise server will straight reject a silent attempt
              prompt: 'login',
              state: generateState()
            })
            .catch(e => {
              clientErrorHandler.logError(e, 'OIDC sign in failed');
            });
        }
      });
    }
  });
}

/**
 * Refresh the tokens of an existing session
 * This is done with silent authentication
 */
function handleTokenRefresh(manager) {
  // Start by cleaning up any stale states
  manager.clearStaleState().then(() => {
    manager.settings.stateStore.getAllKeys().then(keys => {
      // Extra defensive, make sure another auth isn't active
      if (!(keys.length > 2)) {
        manager
          .signinSilent({
            state: generateState()
          })
          .catch(() => {
            manager
              // @TODO: Make this signinRedirect when we support page redirects
              .signinSilent({
                prompt: 'login',
                state: generateState()
              })
              .catch(e => {
                clientErrorHandler.logError(
                  e,
                  'OIDC silent token refresh failed'
                );
              });
          });
      }
    });
  });
}

/**
 * Handle cases where the silent renew failed
 */
function handleRenewError(err) {
  // @TODO: Eventually force prompted login
  clientErrorHandler.logError(err, 'OIDC token renew failed');
}

/**
 * Stores access token on authentication in its own cookie
 */
function handleUserLoaded(user, domain) {
  if (!(user && user.access_token)) {
    return;
  }

  new CookieStorage().setItem('smat', user.access_token, {
    ...COOKIE_DEFAULTS,
    expires: new Date(user.expires_at * 1000),
    domain: `.${domain}`
  });
}

/**
 * The React Context for auth
 */
const AuthContext = createContext();

/**
 * The component wrapped with this provider will have a full
 * OpenID Connect flow ran through automatically in the background
 * This provides an ID token and Access Token for the logged in user
 *
 * @param {Object} props
 * @returns {*}
 */
function AuthenticationProvider({ config, ...props }) {
  let userManager = null;
  const {
    clientId,
    domain,
    enabled = false,
    redirectUri,
    responseMode = 'query'
  } = config;

  // Only configure if oidc is enabled
  if (enabled) {
    try {
      /**
       * Create the standard user manager
       */
      userManager = new UserManager({
        // Configurable settings
        authority: `https://login.${domain}/common/.well-known/openid-configuration`,
        client_id: clientId,
        redirect_uri: redirectUri,
        response_mode: responseMode,

        response_type: 'code',
        scope: 'openid',
        filterProtocolClaims: true,
        loadUserInfo: false,

        // Set user and state storage option
        userStore: new WebStorageStateStore({
          prefix: COOKIE_PREFIX,
          store: new CookieStorage({
            ...COOKIE_DEFAULTS,
            expires: getCookieExpiry(),
            domain: `.${domain}`
          })
        }),
        stateStore: new WebStorageStateStore({
          prefix: COOKIE_PREFIX,
          store: new CookieStorage({
            ...COOKIE_DEFAULTS,
            expires: getCookieExpiry(),
            domain: `.${domain}`,
            sameSite: 'Lax'
          })
        }),
        staleStateAge: 30,

        // May want to monitor eventually for server-side invalidation
        monitorSession: false,
        checkSessionInterval: 5000,

        // Silent renewal settings
        automaticSilentRenew: false,
        silentRequestTimeout: 10000,
        accessTokenExpiringNotificationTime: 60,
        includeIdTokenInSilentRenew: true
      });

      /**
       * Add all events to monitor
       */
      userManager.events.addAccessTokenExpiring(() =>
        handleTokenRefresh(userManager)
      );
      userManager.events.addAccessTokenExpired(() =>
        handleTokenRefresh(userManager)
      );
      userManager.events.addSilentRenewError(err => handleRenewError(err));
      userManager.events.addUserLoaded(user => handleUserLoaded(user, domain));

      /**
       * Make sure there is an existing user from the start
       * Start by cleaning up state
       */
      userManager.clearStaleState().then(() => handleSignIn(userManager));

      // Making sure this doesn't affect the consumer negatively right now
    } catch (err) {
      clientErrorHandler.logError(err, 'Error setting up UserManager');
    }
  }

  return <AuthContext.Provider value={{ userManager }} {...props} />;
}

AuthenticationProvider.propTypes = {
  config: PropTypes.shape({
    clientId: PropTypes.string,
    domain: PropTypes.string,
    redirectUri: PropTypes.string,
    enabled: PropTypes.bool,
    responseMode: PropTypes.string
  }).isRequired
};

/**
 * Hook to get the authentication context
 * The provided userManager can be used to fetch or manage all
 * parts of the OIDC flow
 *
 * @returns {{}|{userManager: {}}}
 */
function useAuthentication() {
  const context = useContext(AuthContext);

  // Make sure context is used properly
  if (!context) {
    throw new Error(
      'useAuthentication must be used within a AuthenticationProvider'
    );
  }

  return context;
}

export {
  AuthenticationProvider,
  useAuthentication,
  handleSignIn,
  handleTokenRefresh,
  handleRenewError,
  handleUserLoaded
};
